import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { playerCardIdentityDisplay } from "@bw/shared/components";

import type { TournamentSanction } from "../api";
import {
  CHECK_LABELS,
  FACT_LABELS,
  formatDateRange,
  formatDateTime,
  formatDay,
} from "../sanctionDisplay";

export function PanelSection({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}
    >
      <Stack direction="row" sx={{ alignItems: "center", mb: 1 }}>
        <Typography
          component="h3"
          sx={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}
        >
          {title}
        </Typography>
        {aside}
      </Stack>
      {children}
    </Box>
  );
}

function FactRow({
  label,
  changed,
  children,
}: {
  label: string;
  changed: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "120px minmax(0, 1fr)",
        columnGap: 1.5,
        py: 0.5,
        fontSize: 13,
      }}
    >
      <Typography
        component="span"
        color="text.secondary"
        sx={{ fontSize: 12.5 }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        {children}
        {changed ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Changed since submitted"
            sx={{ ml: 0.75, height: 20, fontSize: 11 }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function humanize(value: string | null | undefined): string {
  if (!value) return "";
  const text = value.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function ages(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  if (min != null && max != null) return `ages ${min}–${max}`;
  if (max != null) return `ages up to ${max}`;
  if (min != null) return `ages ${min}+`;
  return null;
}

/** Changed fact keys collapsed onto their display rows. */
export function changedFactLabels(
  changed: readonly string[] | null | undefined,
): string[] {
  return [
    ...new Set((changed ?? []).map((key) => FACT_LABELS[key] ?? humanize(key))),
  ];
}

/**
 * A registered referee shows their current account name plus #last4 (resolved
 * by the server, privacy-gated); "—" when withheld or erased. An official
 * without an account shows the name the club entered. Never the user ID.
 */
function RefereeName({ sanction }: { sanction: TournamentSanction }) {
  const referee = sanction.submitted_facts.referee;
  if (referee.user_id) {
    const identity = playerCardIdentityDisplay(
      sanction.referee_display_name ?? "",
      referee.user_id,
    );
    return (
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
        {identity.name}
        {identity.handle ? (
          <Typography
            component="span"
            color="text.secondary"
            sx={{ fontSize: 12.5, fontWeight: 400, ml: 0.5 }}
          >
            {identity.handle}
          </Typography>
        ) : null}
      </Typography>
    );
  }
  return (
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
      {referee.display_name.trim() || "—"}
    </Typography>
  );
}

export function SanctionFactList({
  sanction,
}: {
  sanction: TournamentSanction;
}) {
  const facts = sanction.submitted_facts;
  const changed = new Set(sanction.facts_changed ?? []);
  const venue = [facts.venue_name, facts.venue_address]
    .filter(Boolean)
    .join(", ");
  return (
    <Box>
      <FactRow
        label="Dates"
        changed={changed.has("start_date") || changed.has("end_date")}
      >
        <Typography component="span" sx={{ fontSize: 13 }}>
          {formatDateRange(facts.start_date, facts.end_date)}
        </Typography>
      </FactRow>
      <FactRow
        label="Venue"
        changed={[
          "facility_id",
          "facility_name",
          "venue_name",
          "venue_address",
        ].some((key) => changed.has(key))}
      >
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
          {facts.facility_name}
        </Typography>
        {venue ? (
          <Typography
            component="span"
            color="text.secondary"
            sx={{ fontSize: 12.5, display: "block" }}
          >
            {venue}
          </Typography>
        ) : null}
      </FactRow>
      <FactRow label="Type" changed={changed.has("tournament_type")}>
        <Typography component="span" sx={{ fontSize: 13 }}>
          {humanize(facts.tournament_type)}
        </Typography>
      </FactRow>
      <FactRow label="Referee" changed={changed.has("referee")}>
        <RefereeName sanction={sanction} />
      </FactRow>
      <FactRow label="Events" changed={changed.has("events")}>
        <Typography component="span" sx={{ fontSize: 13 }}>
          {facts.events.length} {facts.events.length === 1 ? "event" : "events"}
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 0.5 }}>
          {facts.events.map((event) => {
            const detail = [
              event.grade ? `Grade ${event.grade}` : null,
              ages(event.min_age, event.max_age),
              (event.draws ?? [])
                .map((draw) => humanize(draw.format))
                .join(", ") || "No draw yet",
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <Box
                component="li"
                key={event.event_id}
                sx={{ py: 0.25, fontSize: 12.5 }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 12.5, fontWeight: 600 }}
                >
                  {event.name}
                </Typography>{" "}
                <Typography
                  component="span"
                  color="text.secondary"
                  sx={{ fontSize: 12.5 }}
                >
                  {detail}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </FactRow>
    </Box>
  );
}

type CheckState = "ok" | "fail" | "waiting" | "pending";

function CheckLine({
  ok,
  label,
  detail,
  pending = false,
  waiting = false,
}: {
  ok: boolean;
  label: string;
  detail: string;
  pending?: boolean;
  waiting?: boolean;
}) {
  const state: CheckState = pending
    ? "pending"
    : ok
      ? "ok"
      : waiting
        ? "waiting"
        : "fail";
  const Icon =
    state === "pending"
      ? RadioButtonUncheckedIcon
      : state === "ok"
        ? CheckCircleIcon
        : ErrorIcon;
  const color = {
    ok: "success.main",
    fail: "error.main",
    waiting: "warning.main",
    pending: "text.disabled",
  }[state];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "22px 120px minmax(0, 1fr)",
        alignItems: "start",
        py: 0.4,
      }}
    >
      <Icon
        fontSize="small"
        aria-hidden="true"
        sx={{ fontSize: 17, mt: "1px", color }}
      />
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        color="text.secondary"
        sx={{ fontSize: 12.5 }}
      >
        {detail}
      </Typography>
    </Box>
  );
}

export function SanctionCheckList({
  checks,
}: {
  checks: NonNullable<TournamentSanction["checks"]>;
}) {
  return (
    <Box role="list" aria-label="Sanction checks">
      {(Object.keys(CHECK_LABELS) as (keyof typeof CHECK_LABELS)[]).map(
        (key) => {
          const copy = CHECK_LABELS[key];
          const ok = checks[key];
          return (
            <Box role="listitem" key={key}>
              <CheckLine
                ok={ok}
                label={copy.label}
                detail={ok ? copy.pass : copy.fail}
              />
            </Box>
          );
        },
      )}
    </Box>
  );
}

/** The four-step status: requested → sanctioned → results submitted → ratings applied. */
export function SanctionSteps({ sanction }: { sanction: TournamentSanction }) {
  const status = sanction.status;
  const sanctioned = [
    "sanctioned",
    "results_submitted",
    "ratings_applied",
    "completed_unrated",
  ].includes(status);
  const submitted = ["results_submitted", "ratings_applied"].includes(status);
  const applied = status === "ratings_applied";
  const conditions = (sanction.conditions ?? []).length;
  return (
    <Box role="list" aria-label="Sanction progress">
      <Box role="listitem">
        <CheckLine
          ok
          label="Requested"
          detail={`${formatDay(sanction.submitted_at)}${
            sanction.submitted_by_display_name
              ? ` by ${sanction.submitted_by_display_name}`
              : ""
          }`}
        />
      </Box>
      <Box role="listitem">
        <CheckLine
          ok={sanctioned}
          pending={!sanctioned}
          label="Sanctioned"
          detail={
            sanctioned
              ? `${formatDay(sanction.decided_at)}${
                  conditions
                    ? `, ${conditions} ${conditions === 1 ? "condition" : "conditions"}`
                    : ""
                }`
              : "Not yet"
          }
        />
      </Box>
      <Box role="listitem">
        <CheckLine
          ok={submitted}
          pending={!submitted && !sanction.results_overdue}
          label="Results submitted"
          detail={
            submitted
              ? `${formatDateTime(sanction.results_submitted_at)}${sanction.results_late ? ", late" : ""}`
              : sanction.results_overdue
                ? `Overdue since ${formatDateTime(sanction.results_due_at)}`
                : `Due ${formatDateTime(sanction.results_due_at)}`
          }
        />
      </Box>
      <Box role="listitem">
        <CheckLine
          ok={applied}
          waiting={status === "results_submitted"}
          pending={!applied && status !== "results_submitted"}
          label="Ratings applied"
          detail={
            applied
              ? formatDay(sanction.ratings_applied_at)
              : status === "results_submitted"
                ? "Waiting for you"
                : status === "completed_unrated"
                  ? "Closed without ratings"
                  : "Not yet"
          }
        />
      </Box>
    </Box>
  );
}
